import { useState, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';
import { Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import NegociosCard from './NegociosCard';
import DrawerChatWhatsapp from 'src/sections/@chats/chat/DrawerChatWhatsapp';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { NEGOCIOS_DESIGN as D } from 'src/pages/dashboard/Negocios/Negocios.design';

// ----------------------------------------------------------------------

const COLUMN_COLORS = ['#7C3AED', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#14b8a6', '#f97316'];

function leadKey(leadId) {
  if (leadId == null) return '';
  if (typeof leadId === 'string' || typeof leadId === 'number') return String(leadId);
  return String(leadId._id || leadId.$oid || leadId.id || leadId);
}

function getLead(leadsKanban, leadId) {
  if (!leadsKanban || leadId == null) return undefined;
  if (leadsKanban[leadId]) return leadsKanban[leadId];
  const key = leadKey(leadId);
  if (leadsKanban[key]) return leadsKanban[key];
  return Object.values(leadsKanban).find((lead) => leadKey(lead?._id) === key);
}

function parseLeadValor(valor) {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  if (valor == null || valor === '') return 0;
  if (typeof valor === 'object') {
    if (typeof valor.valueOf === 'function') {
      const primitive = valor.valueOf();
      if (primitive !== valor && typeof primitive === 'number' && Number.isFinite(primitive)) {
        return primitive;
      }
    }
    return parseLeadValor(valor.$numberDecimal ?? valor.$numberDouble ?? valor.value);
  }
  const cleaned = String(valor).replace(/[R$\s]/gi, '').trim();
  if (!cleaned) return 0;
  const comma = cleaned.includes(',');
  const dotCount = (cleaned.match(/\./g) || []).length;
  let normalized = cleaned;
  if (comma) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (dotCount > 1) {
    const lastDot = cleaned.lastIndexOf('.');
    const decimals = cleaned.slice(lastDot + 1);
    normalized = decimals.length <= 2
      ? `${cleaned.slice(0, lastDot).replace(/\./g, '')}.${decimals}`
      : cleaned.replace(/\./g, '');
  } else if (dotCount === 1 && /^\d{1,3}\.\d{3}$/.test(cleaned)) {
    normalized = cleaned.replace('.', '');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

KanbanColumn.propTypes = {
  column: PropTypes.object,
  index: PropTypes.number,
  filterConvert: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  filterClosed: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onClickTarefa: PropTypes.func,
};

function KanbanColumn({ user, loadingKanban, cliente, column, callbackRefreshLeads, onSelect, onClickTarefa, filterConvert, filterClosed, tagsNegocio, statusVenda }) {
  // Seleciona só leadsKanban (não o slice crm inteiro) para não re-renderizar
  // a coluna em qualquer alteração do crm que não afete os leads em si.
  const leadsKanban = useSelector((state) => state.crm.leadsKanban);
  const { nome, leadIds, id } = column;
  const [openDrawerChatWhatsapp, setOpenDrawerChatWhatsapp] = useState(false);

  const [displayedCount, setDisplayedCount] = useState(5);
  const [isDescending, setIsDescending] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const toggleSortOrder = () => {
    setIsDescending((prev) => !prev);
  };

  // Filtro de status (convertido/fechado) — usado tanto para a lista renderizada
  // quanto para a contagem no cabeçalho (que não deve considerar a busca local).
  const statusFilteredLeadIds = useMemo(() => {
    return (leadIds || []).filter((leadId) => {
      const lead = getLead(leadsKanban, leadId);
      if (!lead) return false;
      if (filterConvert === 1 && lead.convertido !== 1) return false;
      if (filterConvert === 2 && lead.convertido !== 2) return false;
      if (filterClosed === true && lead.closed !== true) return false;
      if (filterClosed === false && lead.closed !== false) return false;
      return true;
    });
  }, [leadIds, leadsKanban, filterConvert, filterClosed]);

  const filteredLeadIds = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    if (!searchLower) return statusFilteredLeadIds;
    return statusFilteredLeadIds.filter((leadId) => {
      const lead = getLead(leadsKanban, leadId);
      const nomeMatch = lead?.nome?.toLowerCase().includes(searchLower) || lead?.nome == null;
      const telefoneMatch = lead?.contato?.telefone?.includes(searchTerm) || false;
      const whatsappMatch = lead?.whatsapp?.numero?.includes(searchTerm) || false;
      return nomeMatch || telefoneMatch || whatsappMatch;
    });
  }, [statusFilteredLeadIds, leadsKanban, searchTerm]);

  const sortedLeadIds = useMemo(() => {
    return [...filteredLeadIds].sort((a, b) => {
      const dateA = new Date(getLead(leadsKanban, a)?.data_modificacao || 0).getTime();
      const dateB = new Date(getLead(leadsKanban, b)?.data_modificacao || 0).getTime();
      return isDescending ? dateB - dateA : dateA - dateB;
    });
  }, [filteredLeadIds, leadsKanban, isDescending]);

  const totalValue = useMemo(() => {
    const seen = new Set();
    return statusFilteredLeadIds.reduce((sum, leadId) => {
      const lead = getLead(leadsKanban, leadId);
      const id = leadKey(lead?._id || leadId);
      if (!id || seen.has(id)) return sum;
      seen.add(id);
      return sum + parseLeadValor(lead?.valor);
    }, 0);
  }, [statusFilteredLeadIds, leadsKanban]);

  const handleLoadMore = () => {
    setDisplayedCount((prev) => Math.min(prev + 10, sortedLeadIds.length));
  };

  // Cor determinística a partir do nome da coluna
  const columnColor = useMemo(() => {
    if (!nome) return COLUMN_COLORS[0];
    let hash = 0;
    for (let i = 0; i < nome.length; i++) {
      hash = nome.charCodeAt(i) + ((hash << 5) - hash);
    }
    return COLUMN_COLORS[Math.abs(hash) % COLUMN_COLORS.length];
  }, [nome]);

  return (
    <>
      <DrawerChatWhatsapp
        open={openDrawerChatWhatsapp}
        onClose={() => setOpenDrawerChatWhatsapp(false)}
        cliente={cliente}
        user={user}
      />

      <div className={D.column.card}>
        <div className={D.column.header}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className={D.column.title}>{nome}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setShowSearch(v => !v)}
                className={D.column.iconBtn}
                title="Pesquisar"
              >
                <Iconify icon="eva:search-outline" width={14} />
              </button>
              <button
                onClick={toggleSortOrder}
                className={D.column.iconBtn}
                title={isDescending ? 'Mais recentes primeiro' : 'Mais antigos primeiro'}
              >
                <Iconify icon={isDescending ? 'octicon:sort-asc-16' : 'octicon:sort-desc-16'} width={14} />
              </button>
            </div>
          </div>

          <div className={D.column.meta}>
            <span className="font-medium">{statusFilteredLeadIds.length} negócio{statusFilteredLeadIds.length !== 1 ? 's' : ''}</span>
            {totalValue > 0 && (
              <>
                <span className="text-[#d4d4d8]">·</span>
                <span className="font-semibold">R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </>
            )}
          </div>

          {showSearch && (
            <div className="relative">
              <Input
                placeholder="Pesquisar..."
                className={D.column.search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              <Iconify icon="eva:search-outline" width={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#a1a1aa]" />
              {searchTerm && (
                <button
                  onClick={() => { setSearchTerm(''); setShowSearch(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#a1a1aa] hover:text-[#18181b]"
                >
                  <Iconify icon="eva:close-outline" width={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Cards Area */}
        <Droppable droppableId={id} type="task">
          {(provided, snapshot) => (
            <div
              className={snapshot.isDraggingOver ? D.column.bodyOver : D.column.body}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {loadingKanban ? (
                // Skeleton loading
                <div className="space-y-2.5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-slate-50 dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 animate-pulse">
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-3 w-16 bg-gray-200 dark:bg-[#383838] rounded" />
                        <div className="h-3 w-12 bg-gray-200 dark:bg-[#383838] rounded" />
                      </div>
                      <div className="h-4 w-3/4 bg-gray-200 dark:bg-[#383838] rounded mb-2" />
                      <div className="h-3 w-1/2 bg-gray-200 dark:bg-[#383838] rounded mb-3" />
                      <div className="flex items-center justify-between pt-2 border-t border-gray-50 dark:border-[#383838]/50">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-[#383838]" />
                          <div className="h-3 w-20 bg-gray-200 dark:bg-[#383838] rounded" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : sortedLeadIds.length === 0 ? (
                <div className="bg-slate-50 dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 border-dashed">
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-3 w-16 bg-gray-200 dark:bg-[#383838] rounded opacity-50" />
                    <div className="h-3 w-12 bg-gray-200 dark:bg-[#383838] rounded opacity-50" />
                  </div>
                  <div className="h-4 w-3/4 bg-gray-200 dark:bg-[#383838] rounded mb-2 opacity-50" />
                  <div className="h-3 w-1/2 bg-gray-200 dark:bg-[#383838] rounded mb-3 opacity-50" />
                  <div className="flex items-center justify-between pt-2 border-t border-gray-50 dark:border-[#383838]/50">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-[#383838] opacity-50" />
                      <div className="h-3 w-20 bg-gray-200 dark:bg-[#383838] rounded opacity-50" />
                    </div>
                  </div>
                  <div className="text-center mt-4 text-gray-300 dark:text-[#ABABAD]/30">
                    <span className="text-[11px] font-medium">
                      {searchTerm ? 'Nenhum resultado' : 'Arraste leads aqui'}
                    </span>
                  </div>
                </div>
              ) : (
                leadsKanban &&
                sortedLeadIds
                  .slice(0, displayedCount)
                  .map((cardId, index) => (
                    <NegociosCard
                      callbackRefreshLeads={callbackRefreshLeads}
                      onSelect={onSelect}
                      onClickTarefa={onClickTarefa}
                      cardId={cardId}
                      userId={user.id}
                      rules={user.rules}
                      key={cardId}
                      card={getLead(leadsKanban, cardId)}
                      index={index}
                      tagsNegocio={tagsNegocio}
                      statusVenda={statusVenda}
                    />
                  ))
              )}
              {provided.placeholder}

              {/* Load more button */}
              {displayedCount < sortedLeadIds.length && (
                <div className="flex justify-center pt-2">
                  <button
                    onClick={handleLoadMore}
                    className={D.column.loadMore}
                  >
                    Carregar mais ({sortedLeadIds.length - displayedCount} restantes)
                  </button>
                </div>
              )}
            </div>
          )}
        </Droppable>
      </div>
    </>
  );
}

export default memo(KanbanColumn);
